import { useMemo, useState } from "react";
import Plot from "react-plotly.js";

import Card from "components/atoms/Card";

type UsageRoute = {
  routeKey: string;
  displayName: string;
  category: string;
  totalCalls: number;
  totalTokens: number;
  avgDailyCalls: number;
  avgDailyTokens: number;
  totalCostUSD?: number;
};

const FAMILY_COLORS: Record<string, string> = {
  Vision: "#16a34a",
  "Text AI": "#ea580c",
  Search: "#2563eb",
  "Food Database": "#0891b2",
  "Advisor / Chat": "#7c3aed",
  Catalog: "#db2777",
  Affiliates: "#0f766e",
  Other: "#6b7280",
};

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  if (num === 0) return "0";
  if (num < 1) return num.toFixed(2);
  return num.toFixed(0);
};

const formatCurrency = (amount: number): string => {
  if (amount < 0.01 && amount > 0) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(2)}`;
};

export const routeFamily = (category: string, routeKey = ""): string => {
  const haystack = `${category} ${routeKey}`.toLowerCase();
  if (haystack.includes("vision")) return "Vision";
  if (haystack.includes("semantic") || haystack.includes("search"))
    return "Search";
  if (haystack.includes("catalog") || haystack.includes("chain"))
    return "Catalog";
  if (haystack.includes("affiliate")) return "Affiliates";
  if (
    haystack.includes("advisor") ||
    haystack.includes("chat") ||
    haystack.includes("easyai")
  )
    return "Advisor / Chat";
  if (haystack.includes("food")) return "Food Database";
  if (haystack.includes("ai tool") || haystack.includes("ai tools"))
    return "Text AI";
  return "Other";
};

type Props = {
  routes: UsageRoute[];
  days: number;
  tokenLabel?: string;
};

const RouteUsageOverview = ({ routes, days, tokenLabel = "tokens" }: Props) => {
  const [metric, setMetric] = useState<"calls" | "tokens">("calls");

  const used = useMemo(
    () =>
      routes
        .filter((route) => route.totalCalls > 0 || route.totalTokens > 0)
        .sort((a, b) =>
          metric === "tokens"
            ? b.totalTokens - a.totalTokens
            : b.totalCalls - a.totalCalls
        ),
    [routes, metric]
  );

  const families = useMemo(() => {
    const map = new Map<
      string,
      { calls: number; tokens: number; routes: number }
    >();
    used.forEach((route) => {
      const family = routeFamily(route.category, route.routeKey);
      const current = map.get(family) || { calls: 0, tokens: 0, routes: 0 };
      current.calls += route.totalCalls;
      current.tokens += route.totalTokens;
      current.routes += 1;
      map.set(family, current);
    });
    return Array.from(map.entries())
      .map(([name, value]) => ({ name, ...value }))
      .sort((a, b) =>
        metric === "tokens" ? b.tokens - a.tokens : b.calls - a.calls
      );
  }, [used, metric]);

  const topRoutes = used.slice(0, 12);
  const totalCalls = used.reduce((sum, route) => sum + route.totalCalls, 0);
  const totalTokens = used.reduce((sum, route) => sum + route.totalTokens, 0);
  const totalCost = used.reduce(
    (sum, route) => sum + (route.totalCostUSD || 0),
    0
  );

  if (used.length === 0) {
    return (
      <Card className="p-6">
        <h4 className="font-semibold text-gray-900">Usage by route</h4>
        <p className="mt-2 text-sm text-gray-500">
          No API calls in this period yet. Usage by vision, text, search, and
          other groups will show up here after the first requests.
        </p>
      </Card>
    );
  }

  const familyValues = families.map((family) =>
    metric === "tokens" ? family.tokens : family.calls
  );
  const routeValues = topRoutes.map((route) =>
    metric === "tokens" ? route.totalTokens : route.totalCalls
  );

  return (
    <Card className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h4 className="font-semibold text-gray-900">Usage by route</h4>
          <p className="text-sm text-gray-500">
            {used.length} distinct routes over {days} days, with path IDs
            collapsed. Includes catalog/chain routes such as grocery
            replacements when this account has called them.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
          <button
            type="button"
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              metric === "calls"
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => setMetric("calls")}
          >
            Calls
          </button>
          <button
            type="button"
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              metric === "tokens"
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
            onClick={() => setMetric("tokens")}
          >
            {tokenLabel}
          </button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 mb-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">
            Routes used
          </p>
          <p className="text-xl font-semibold text-gray-900">{used.length}</p>
          <p className="text-xs text-gray-500">
            {formatNumber(totalCalls)} calls · {formatNumber(totalTokens)}{" "}
            {tokenLabel.toLowerCase()}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">
            Top group
          </p>
          <p className="text-xl font-semibold text-gray-900">
            {families[0]?.name || "—"}
          </p>
          <p className="text-xs text-gray-500">
            {families[0]
              ? `${formatNumber(
                  metric === "tokens" ? families[0].tokens : families[0].calls
                )} ${metric} · ${families[0].routes} routes`
              : "No grouped usage"}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
          <p className="text-[11px] uppercase tracking-wide text-gray-500">
            Top route
          </p>
          <p className="text-xl font-semibold text-gray-900 truncate">
            {used[0]?.displayName || "—"}
          </p>
          <p className="text-xs text-gray-500">
            {used[0] ? `${formatNumber(used[0].avgDailyCalls)} calls/day` : ""}
            {totalCost > 0 ? ` · ${formatCurrency(totalCost)} ref.` : ""}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="px-1 text-sm font-semibold text-gray-900">
            By product group
          </p>
          <p className="px-1 text-xs text-gray-500 mb-1">
            Vision, text, search, catalog, and the rest of this account&apos;s
            mix.
          </p>
          <Plot
            data={[
              {
                type: "pie",
                hole: 0.45,
                labels: families.map((family) => family.name),
                values: familyValues,
                marker: {
                  colors: families.map(
                    (family) =>
                      FAMILY_COLORS[family.name] || FAMILY_COLORS.Other
                  ),
                },
                textinfo: "label+percent",
                textposition: "inside",
                insidetextorientation: "horizontal",
                hovertemplate:
                  "%{label}<br>%{value:,} " +
                  metric +
                  "<br>%{percent}<extra></extra>",
              },
            ]}
            layout={{
              height: 300,
              margin: { t: 8, r: 8, b: 8, l: 8 },
              paper_bgcolor: "transparent",
              showlegend: false,
            }}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: "100%", height: "300px" }}
          />
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3">
          <p className="px-1 text-sm font-semibold text-gray-900">Top routes</p>
          <p className="px-1 text-xs text-gray-500 mb-1">
            Same period as the table below. Parameterized IDs are already
            collapsed into one row per route.
          </p>
          <Plot
            data={[
              {
                type: "bar",
                orientation: "h",
                y: topRoutes.map((route) =>
                  route.displayName.length > 28
                    ? `${route.displayName.slice(0, 28)}…`
                    : route.displayName
                ),
                x: routeValues,
                marker: {
                  color: topRoutes.map(
                    (route) =>
                      FAMILY_COLORS[
                        routeFamily(route.category, route.routeKey)
                      ] || FAMILY_COLORS.Other
                  ),
                },
                customdata: topRoutes.map((route) => [
                  route.displayName,
                  routeFamily(route.category, route.routeKey),
                  route.totalCalls,
                  route.totalTokens,
                  route.avgDailyCalls,
                ]),
                hovertemplate:
                  "%{customdata[0]}<br>%{customdata[1]}<br>Calls: %{customdata[2]:,}<br>Tokens: %{customdata[3]:,}<br>Avg/day: %{customdata[4]:.1f}<extra></extra>",
              },
            ]}
            layout={{
              height: Math.max(300, 28 * topRoutes.length + 60),
              margin: { t: 8, r: 16, b: 32, l: 140 },
              paper_bgcolor: "transparent",
              plot_bgcolor: "transparent",
              yaxis: { autorange: "reversed", tickfont: { size: 11 } },
              xaxis: {
                title: metric === "tokens" ? tokenLabel : "Calls",
                rangemode: "tozero",
              },
            }}
            config={{ displayModeBar: false, responsive: true }}
            style={{
              width: "100%",
              height: `${Math.max(300, 28 * topRoutes.length + 60)}px`,
            }}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {families.map((family) => (
          <span
            key={family.name}
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-700"
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{
                backgroundColor:
                  FAMILY_COLORS[family.name] || FAMILY_COLORS.Other,
              }}
            />
            {family.name}
            <span className="text-gray-500">
              {formatNumber(metric === "tokens" ? family.tokens : family.calls)}
            </span>
          </span>
        ))}
      </div>
    </Card>
  );
};

export default RouteUsageOverview;
